import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaTrophy, FaUsers, FaCalendarCheck, FaMedal, FaArrowRight } from 'react-icons/fa';
import './About.css';
import { Helmet } from 'react-helmet';

const AboutUs = () => {
  const stats = [
    { icon: <FaTrophy />, number: '500+', label: 'Championships Won' },
    { icon: <FaUsers />, number: '2000+', label: 'Students Trained' },
    { icon: <FaCalendarCheck />, number: '15+', label: 'Years Experience' },
    { icon: <FaMedal />, number: '50+', label: 'National Players' },
  ];

  const values = [
    {
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from coaching to facility management.',
      icon: '🏆'
    },
    {
      title: 'Integrity',
      description: 'We operate with honesty and transparency, building trust with our students and their families.',
      icon: '🤝'
    },
    {
      title: 'Passion',
      description: 'Our love for badminton drives us to help every student discover their potential.',
      icon: '❤️'
    },
    {
      title: 'Innovation',
      description: 'We continuously evolve our training methods to stay at the forefront of badminton coaching.',
      icon: '💡'
    },
  ];

  return (
    <>
      <Helmet>
        <title>About Us</title>
      </Helmet>
      <div className="about-page">
        <div className="page-header">
          <div className="container">
            <h1>About Us</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>About Us</span>
            </div>
          </div>
        </div>

        <section className="about-intro section">
          <div className="container">
            <div className="about-intro-grid">
              <div className="about-intro-content">
                <span className="section-subtitle">Who We Are</span>
                <h2>Welcome to Imperial Badminton Academy</h2>
                <p className="lead">
                  Imperial Badminton Academy is more than just a training facility - we are a community
                  of passionate badminton enthusiasts dedicated to developing champions both on and off the court.
                </p>
                <p>
                  Imperial Badminton Academy is a dedicated center of excellence committed to the growth, development, and success of badminton players at every level. Established with the vision of creating disciplined athletes and future champions, our academy stands as a symbol of quality coaching, modern training methods, and a passion for the sport.
                </p>
                <p>
                  At Imperial Badminton Academy, we believe badminton is more than just a game—it is a powerful tool for building character, confidence, physical fitness, and mental strength. Our goal is to provide a professional, structured, and motivating environment where players can learn, train, and evolve into complete athletes.
                </p>
                <div className="about-features-list">
                  <div className="feature-item">
                    <FaCheckCircle />
                    <span>8 International Standard Courts</span>
                  </div>
                  <div className="feature-item">
                    <FaCheckCircle />
                    <span>Certified Professional Coaches</span>
                  </div>
                  <div className="feature-item">
                    <FaCheckCircle />
                    <span>Scientific Training Methods</span>
                  </div>
                  <div className="feature-item">
                    <FaCheckCircle />
                    <span>Modern Fitness Center</span>
                  </div>
                </div>
              </div>
              <div className="about-intro-images">
                <div className="intro-img-main">
                  <img src="img/About.jpg" alt="Academy Training" />
                </div>
              </div>
            </div>
            <div className="about-intro-grid">
              <div className="about-intro-content">
                <h3>Our Journey</h3>
                <p>
                  Imperial Badminton Academy was founded with the objective of offering world-class badminton training under expert guidance. Recognizing the growing interest in badminton and the need for systematic coaching, we created an academy that combines traditional coaching values with modern sports science.
                </p>
                <p>
                  From grassroots-level beginners to competitive players, we have successfully built a training ecosystem that encourages continuous improvement, discipline, and excellence.
                </p>
              </div>
              <div className="about-intro-images">
                <div className="about-intro-content">
                  <h3>Our Training Environment</h3>
                  <p>We provide a safe, positive, and professional training atmosphere that inspires players to give their best every day. Our academy is equipped with international-standard indoor courts, proper lighting, and high-quality playing surfaces to ensure optimal performance and safety.</p>
                  <p>Every player is trained in an environment that promotes focus, teamwork, and mutual respect, helping them grow both on and off the court.</p>
                </div>
              </div>
            </div>
            <div className="about-intro-grid">
              <div className="about-intro-content">
                <h3>Coaching Philosophy</h3>
                <p>
                  Our coaching philosophy is centered on individual development. We understand that every player is unique, and therefore, our training approach is customized to suit individual skill levels, learning pace, and performance goals.
                </p>
              </div>
              <div className="about-intro-images">
                <div className="about-intro-content">
                  <h3>Commitment to Excellence</h3>
                  <p>Excellence is at the heart of everything we do at Imperial Badminton Academy. We maintain high training standards and continuously upgrade our coaching methods to match evolving international badminton practices.</p>
                  <p>Our commitment goes beyond winning matches—we aim to develop confident individuals who demonstrate discipline, dedication, and sportsmanship in every aspect of life.</p>
                </div>
              </div>
            </div>
            <div className="about-intro-grid">
              <div className="about-intro-content">
                <h3>Player-Centric Approach</h3>
                <p>
                  We strongly believe that player success comes from personalized attention and guidance. Our academy maintains optimal coach-to-player ratios, allowing focused training, regular feedback, and performance evaluation.
                </p>
                <p>
                  We encourage open communication between coaches, players, and parents to ensure transparency, trust, and shared progress.
                </p>
              </div>
              <div className="about-intro-images">
                <div className="about-intro-content">
                  <h3>Building Future Champions</h3>
                  <p>Imperial Badminton Academy is passionate about identifying and nurturing young talent. We guide players through every stage of their badminton journey, helping them develop a strong foundation, competitive mindset, and professional attitude required to succeed at higher levels.</p>
                  <p>Our aim is not only to create skilled badminton players but also to shape responsible, confident, and goal-oriented individuals.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="about-stats section bg-primary">
          <div className="container">
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-icon">{stat.icon}</div>
                  <h3>{stat.number}</h3>
                  <p>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="about-values section">
          <div className="container">
            <div className="section-title">
              <h2>What Drives Us</h2>
              <p>
                Our values form the foundation of everything we do at Imperial Badminton Academy.
              </p>
            </div>
            <div className="values-grid">
              {values.map((value, index) => (
                <div key={index} className="value-card">
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutUs;
